import { useSessionStorage } from "./useSessionStorage";
import { useState } from "react";
import actions from "../config.json";
import { getExperienceParams } from "../utils/experienceContext";

export const usePassions = (experienceType) => {
  const storageKey = "storage";
  const { setItem, getItem } = useSessionStorage(experienceType, {
    key: storageKey,
    value: {
      golf: 0,
      beach: 0,
      ski: 0,
      culinary: 0,
      culture: 0,
      music: 0,
      spa: 0,
      paddle: 0,
      bike: 0,
      fish: 0,
      dive: 0,
      safari: 0,
      surf: 0,
      hike: 0,
    },
  });
  const [passions, setPassions] = useState(getItem(storageKey));

  const selectPassion = (passion) => {
    const value = { ...getItem(storageKey) };
    value[passion] = 1;
    setItem(storageKey, value);
    setPassions(value);
  };

  const tapPassion = (passion, maxTaps = 2, val) => {
    const value = { ...getItem(storageKey) };
    let taps = value[passion];

    if (taps >= maxTaps) {
      taps = val ?? 0;
    } else {
      taps += val ?? 1;
    }

    value[passion] = taps;
    setItem(storageKey, value);
    setPassions(value);
    return taps;
  };

  const getPassionValue = (passion) => {
    return getItem(storageKey)[passion] ?? 0;
  };

  const unselectPassion = (passion) => {
    const value = { ...getItem(storageKey) };
    value[passion] = 0;
    setItem(storageKey, value);
    setPassions(value);
  };

  const dislikePassion = (passion) => {
    const value = { ...getItem(storageKey) };
    value[passion] = -1;
    setItem(storageKey, value);
    setPassions(value);
  };

  const isPassionSelected = (passion) => {
    return (getItem(storageKey)?.[passion] || 0) > 0;
  };

  const isAnyPassionSelected = () => {
    return Object.entries(passions).find(([, value]) => value > 0) != null;
  };

  const getSelectedPassionsAsArray = () => {
    const passions = getItem(storageKey);
    return Object.entries(passions)
      .filter(([, value]) => value > 0)
      .map(([key, value]) => {
        if (key === "golf" || key === "spa") {
          return value >= 2 ? `${key}-love` : `${key}-like`;
        }
        return key;
      });
  };

  const getSelectedPassionsAsFlatArray = () => {
    const passions = getItem(storageKey);
    return Object.entries(passions)
      .filter(([, value]) => value > 0)
      .map(([key]) => key);
  };

  const savePassionsToServer = async (acid) => {
    const passions = getItem(storageKey);
    const Category = Object.freeze({
      GOLF: "GOLF_LOV",
      SPA: "SPA_LOV",
      FITNESS: "FITNESS_LOV",
      INTERESTS_AND_HOBBIES: "INTERESTS_AND_HOBBIES_LOV",
    });

    const Groups = Object.freeze({
      ENTHU: "ENTHU",
      CASUAL: "CASUAL",
      BEACH: "BCH",
      WINTER_ACTIVITIES: "WNTRACT",
      CULINARY: "COOK",
      CULTURE: "CULTR",
      MUSIC: "MUSIC",
      NATURE: "NATR",
      SPORT: "SPORT",
      CYCLING: "CYCL",
      HIKING: "HIKWLK",
      KAYAK_CANOE: "KAYAKCANOE",
      SURF: "SURF",
      SCUBA_SNORKEL: "SCUBASNORKEL",
      SAFARI: "SAFARI",
      FISH: "FISH",
    });

    const data = [
      {
        category: Category.GOLF,
        values: [],
        dislike: false,
      },
      {
        category: Category.SPA,
        values: [],
        dislike: false,
      },
      {
        category: Category.FITNESS,
        values: [],
        dislikes: [],
      },
      {
        category: Category.INTERESTS_AND_HOBBIES,
        values: [],
        dislikes: [],
      },
    ];

    function pushValueByCategoryName(data, categoryName, value) {
      const category = data.find((item) => item.category === categoryName);
      if (category) {
        category.values.push(value);
      }
    }

    Object.entries(passions).forEach(([key, value]) => {
      if (value > 0) {
        switch (key) {
          case "golf":
            pushValueByCategoryName(
              data,
              Category.GOLF,
              value > 1 ? Groups.ENTHU : Groups.CASUAL,
            );
            break;
          case "spa":
            pushValueByCategoryName(
              data,
              Category.SPA,
              value > 1 ? Groups.ENTHU : Groups.CASUAL,
            );
            break;
          case "beach":
            pushValueByCategoryName(
              data,
              Category.INTERESTS_AND_HOBBIES,
              Groups.BEACH,
            );
            break;
          case "ski":
            pushValueByCategoryName(
              data,
              Category.INTERESTS_AND_HOBBIES,
              Groups.WINTER_ACTIVITIES,
            );
            break;
          case "culinary":
            pushValueByCategoryName(
              data,
              Category.INTERESTS_AND_HOBBIES,
              Groups.CULINARY,
            );
            break;
          case "culture":
            pushValueByCategoryName(
              data,
              Category.INTERESTS_AND_HOBBIES,
              Groups.CULTURE,
            );
            break;
          case "music":
            pushValueByCategoryName(
              data,
              Category.INTERESTS_AND_HOBBIES,
              Groups.MUSIC,
            );
            break;
          case "paddle":
            pushValueByCategoryName(
              data,
              Category.INTERESTS_AND_HOBBIES,
              Groups.KAYAK_CANOE,
            );
            break;
          case "bike":
            pushValueByCategoryName(data, Category.FITNESS, Groups.CYCLING);
            break;
          case "fish":
            pushValueByCategoryName(data, Category.FITNESS, Groups.FISH);
            break;
          case "dive":
            pushValueByCategoryName(
              data,
              Category.FITNESS,
              Groups.SCUBA_SNORKEL,
            );
            break;
          case "safari":
            pushValueByCategoryName(
              data,
              Category.INTERESTS_AND_HOBBIES,
              Groups.SAFARI,
            );
            break;
          case "surf":
            pushValueByCategoryName(data, Category.FITNESS, Groups.SURF);
            break;
          case "hike":
            pushValueByCategoryName(data, Category.FITNESS, Groups.HIKING);
            break;
          default:
        }
      }

      if (value < 0) {
        // explicit dislikes – only Video/Video3 experiences might set this for now (MPP-1692)
        switch (key) {
          case "golf":
            data[0].dislike = true;
            break;
          case "spa":
            data[1].dislike = true;
            break;
          case "hike":
            data[2].dislikes.push(Groups.HIKING);
            break;
          case "bike":
            data[2].dislikes.push(Groups.CYCLING);
            break;
          case "beach":
            data[3].dislikes.push(Groups.BEACH);
            break;
          case "ski":
            data[3].dislikes.push(Groups.WINTER_ACTIVITIES);
            break;
          case "culinary":
            data[3].dislikes.push(Groups.CULINARY);
            break;
          case "culture":
            data[3].dislikes.push(Groups.CULTURE);
            break;
          case "music":
            data[3].dislikes.push(Groups.MUSIC);
            break;
          case "paddle":
            data[3].dislikes.push(Groups.BEACH);
            break;
          case "fish":
            data[3].dislikes.push(Groups.NATURE);
            break;
          case "dive":
            data[3].dislikes.push(Groups.SPORT);
            break;
          case "safari":
            data[3].dislikes.push(Groups.NATURE);
            break;
          case "surf":
            data[3].dislikes.push(Groups.BEACH);
            break;
          default:
        }
      }
    });

    const actionUrl = actions["marriott-passions-v3/save-passions"];

    try {
      await fetch(`${actionUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          acid: acid,
          passions: data,
          ...getExperienceParams(),
        }),
      });
    } catch (err) {
      console.error(err);
    }
  };

  return {
    selectPassion,
    unselectPassion,
    dislikePassion,
    tapPassion,
    isPassionSelected,
    isAnyPassionSelected,
    getPassionValue,
    getSelectedPassionsAsArray,
    getSelectedPassionsAsFlatArray,
    savePassionsToServer,
  };
};
